import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useEffect } from "react";

import { clearShopRetailId, getLoginToken, getShopRetailId, setShopRetailId } from "../core/retailShop.core";
import useRetailShops from "./useRetailShops";

export default function usePosInit() {
  useInitLoginRoute();
  useInitRetailShopIdFromQuery();
  useInitRetailShopIdDefault();
  useSyncSessionAndCookies();
}

function useInitLoginRoute() {
  const loginToken = getLoginToken();
  const { push } = useRouter();

  useEffect(() => {
    if (!loginToken) {
      push({
        pathname: process.env.NEXT_PUBLIC_ADMIN_URL + "/login",
        query: {
          route: process.env.NEXT_PUBLIC_POS_URL,
        },
      });
    }

    // eslint-disable-next-line
  }, []);
}

// ultimate override to set the retailShopId from query to cookies
function useInitRetailShopIdFromQuery() {
  const { isReady, query } = useRouter();
  const { data: retailShops, isLoading, isError } = useRetailShops();

  useEffect(() => {
    if (!isReady || !query.retailShopId) return;
    if (isLoading) return; // Wait for data to load

    if (isError || !retailShops) {
      console.warn("Failed to load retail shops for query param initialization");
      return; // Keep existing cookie on error
    }

    if (query.isClearCart === "true") {
      Cookies.remove("posCartId");
    }

    // query.shopId is deprecated - shopId is extracted from retail shop data
    // Don't clear existing cookie - just try to update if retail exists
    const retail = retailShops.find((r) => r.id === query.retailShopId);
    if (retail) {
      // Only update if we successfully found the retail
      // shopId comes from the retail object, not from query params
      setShopRetailId(retail.shopId, retail.id);
    }
    // If retail not found, do nothing - keep existing cookie (minimize risk)
  }, [isReady, query, retailShops, isLoading, isError]);
}

// when retailShopId doesn't exist, select the first retail shop
function useInitRetailShopIdDefault() {
  const { isReady, query } = useRouter();
  const shopRetail = getShopRetailId();
  const { data: retailShops, isLoading } = useRetailShops();

  useEffect(() => {
    if (!isReady) return;
    if (query.retailShopId !== undefined) return; // query override takes precedence
    if (isLoading) return; // Wait for data to load - prevent race condition
    if (!retailShops || retailShops.length === 0) return;

    if (!shopRetail) {
      // Case 1: No cookie - set first retail
      const firstRetail = retailShops[0];
      setShopRetailId(firstRetail.shopId, firstRetail.id);
    } else {
      // Case 2: Cookie exists - validate retailId is still valid
      const currentRetail = retailShops.find((r) => r.id === shopRetail.retailId);

      if (!currentRetail) {
        // Invalid - clear and set first
        clearShopRetailId();
        const firstRetail = retailShops[0];
        setShopRetailId(firstRetail.shopId, firstRetail.id);
      }
      // If valid, do nothing - cookie is correct
    }
  }, [isReady, query, shopRetail, retailShops, isLoading]);
}

// whenever a tab is visited/clicked, get the shopId and retailId from the sessionStorage
// on that tab and then sync that with the Cookies storage see docs pos.md Shop Selection
function useSyncSessionAndCookies() {
  useEffect(() => {
    function handleRetailId() {
      const tabShopRetailId = sessionStorage.getItem("shopRetailId");
      const cookiesShopRetailId = Cookies.get("shopRetailId");

      Cookies.remove("posCartId");

      if (tabShopRetailId !== null) {
        // Tab has its own value - sync to cookies
        const [shopId, retailId] = tabShopRetailId.split("_");
        if (shopId && retailId) {
          setShopRetailId(shopId, retailId);
        }
      } else if (cookiesShopRetailId) {
        // Tab is empty - sync from cookies to sessionStorage
        const [shopId, retailId] = cookiesShopRetailId.split("_");
        if (shopId && retailId) {
          setShopRetailId(shopId, retailId);
        }
      }
    }

    window.addEventListener("visibilitychange", handleRetailId);
    return () => {
      window.removeEventListener("visibilitychange", handleRetailId);
    };
  }, []);
}
